import React, { useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';

import { type OnboardingState, areAllFieldsFilled } from './onboardingReducer';
import { OnboardingCheckBox } from './SharedUI';

const ESignAgreement = `This Electronic Communication Disclosure (“E-SIGN Agreement”) applies to all communications and/or disclosures that we are legally required to provide to you in writing in connection with your Account and any related products and services (“Communications”). This E-SIGN Agreement supplements and is to be construed in accordance with the terms contained in the applicable terms of service (“Agreement”) you received from Fragment Foundries, Inc.
The words "we," "us," and "our" refer to Fragment Foundries, Inc. The words "you" and "your" mean the account holder. "Account" means the account you have with us.
1. Consent for Electronic Signatures, Records and Disclosures
Your consent to the electronic delivery of disclosures will be required before you can begin using your Account. If you are unable or unwilling to provide such consent, please do not enroll for an Account. Once you have activated your Account, you may withdraw your consent for E-SIGN and request paper communications and/or disclosures as noted below.
We suggest you read this document and print a copy for your reference.
2. Scope of Communications to Be Provided in Electronic Form
When you use a product or service to which this E-SIGN Agreement applies, you agree that we may provide you with any Communications in electronic format, and that we may discontinue sending paper Communications to you, unless and until you withdraw your consent as described below. Your consent to receive electronic communications and transactions includes, but is not limited to:
All legal and regulatory disclosures and communications associated with your Account and any related products and services
Your Agreement and any notices about a change in the Agreement
Privacy policies and notices
Error resolution policies and notices
Responses to claims filed in connection with your Account
Notices regarding insufficient funds or negative balances
3. Method of Providing Communications to You in Electronic Form
All Communications that we provide to you in electronic form will be provided by access to our website or by email.
4. How to Update Your Records
It is your responsibility to provide us with a true, accurate and complete email address, your contact information, and other information related to this E-SIGN Agreement and your Account, and to maintain and update promptly any changes in this information. You may update your information by logging into your Account and updating your profile.
5. Hardware and Software Requirements
To access, view, and retain electronic Communications that we make available to you, you must have the following software and hardware: A PC or Mac compatible computer or other device capable of accessing the Internet and an Internet Browser software program that supports at least 128 bit encryption. To read some documents, you may need a PDF file reader. If these requirements change while you have an active Account, and the change creates a material risk that you may not be able to receive Communications electronically, we will notify you of these changes. You will need a printer or a long-term storage device, such as your computer’s disk drive, to retain a copy of the E-Sign Agreement for future reference.
6. Requesting Paper Copies
We will not send you a paper copy of any Communication unless you request it or we otherwise deem it appropriate to do so. You can obtain a paper copy of an electronic Communication by printing it yourself or by requesting that we mail you a paper copy, provided that such request is made within a reasonable time after we first provided the electronic Communication to you. To request a paper copy, contact us by mailing your request to 416 W. 13th Street #304 New York, NY 10014.
We reserve the right, but assume no obligation, to provide a paper (instead of electronic) copy of any Communication that you have authorized us to provide electronically. For the avoidance of doubt, requesting a paper copy of any Communication, in and of itself, will not be treated as withdrawal of consent to receive electronic Communications.
7. Communications in Writing
All Communications in either electronic or paper format from us to you will be considered "in writing." You should print or download for your records a copy of this E-Sign Agreement and any other Communication that is important to you.
8. Federal Law
You acknowledge and agree that your consent to electronic Communications is being provided in connection with a transaction affecting interstate commerce that is subject to the federal Electronic Signatures in Global and National Commerce Act, and that you and we both intend that the Act apply to the fullest extent possible to validate our ability to conduct business with you by electronic means.
9. Termination/Changes
We reserve the right, in our sole discretion, to discontinue the provision of your electronic Communications, or to terminate or change the terms and conditions on which we provide electronic Communications. We will provide you with notice of any such termination or change as required by law.
10. How to Withdraw Consent
You may withdraw your consent to receive Communications in electronic form at any time by sending us a written request by mail to 416 W. 13th Street #304 New York, NY 10014. If you withdraw your consent, your Account and all associated cards will be closed in accordance with the Agreement and we will send subsequent required communications and disclosures to you in writing to the most current address we have on file.
We will not impose any fee to process the withdrawal of your consent to receive electronic Communications. Any withdrawal of your consent to receive electronic Communications will be effective only after we have a reasonable period of time to process your withdrawal. In the meantime, you will continue to receive Communications in electronic form. If you withdraw your consent, the legal validity and enforceability of prior Communications delivered in electronic form will not be affected.
`;

type ApplicationStatus = 'incomplete' | 'completed' | 'pending' | 'rejected';

const ApplicationStatusIncompleteView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>
      Your application is incomplete. Please complete all fields before
      submitting.
    </span>
  </div>
);

const ApplicationStatusCompletedView = () => {
  const [isCertified, setIsCertified] = useState(false);
  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <span className="text-main">Electronic Signature Agreement</span>
      <div className="flex flex-col w-full h-f24 overflow-y-scroll border-main-300 border-[0.5px] bg-negative p-f1 gap-y-f2">
        <span>{ESignAgreement}</span>
      </div>
      <OnboardingCheckBox
        checked={isCertified}
        label="I agree to the use of an electronic signature."
        onClick={() => {
          setIsCertified(!isCertified);
        }}
      />

      <div className="flex flex-col w-full h-f24 items-center justify-center border-main-300 border-[0.5px] bg-negative p-f1 gap-y-f2">
        <span>Bank Disclosures</span>
      </div>
      <OnboardingCheckBox
        checked={isCertified}
        label="I certify that all the information provided is accurate and complete."
        onClick={() => {
          setIsCertified(!isCertified);
        }}
      />
      <span>
        Clicking the button below will submit your application for review.
      </span>
      <PrimaryButtonV2 onClick={() => {}}>Submit</PrimaryButtonV2>
    </div>
  );
};

const ApplicationStatusPendingView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>
      Your application has been submitted. You will be notified when it is
      approved or rejected.
    </span>
  </div>
);

const ApplicationStatusRejectedView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>Your application has been sent for manual review.</span>
    <span>We will reach out if additional details are needed.</span>
    <PrimaryButtonV2 onClick={() => {}}>Return to Dashboard</PrimaryButtonV2>
  </div>
);

export const ApplicationStatusView = ({
  onboardingState,
}: {
  onboardingState: OnboardingState;
}) => {
  const status: ApplicationStatus = useMemo(() => {
    return 'completed';
    if (!areAllFieldsFilled(onboardingState)) {
      return 'incomplete';
    }
    return 'completed';
  }, [onboardingState]);

  const statusView = useMemo(
    () =>
      match<ApplicationStatus>(status)
        .with('incomplete', () => <ApplicationStatusIncompleteView />)
        .with('completed', () => <ApplicationStatusCompletedView />)
        .with('pending', () => <ApplicationStatusPendingView />)
        .with('rejected', () => <ApplicationStatusRejectedView />)
        .exhaustive(),
    [status]
  );
  return <div className="flex flex-col h-fit gap-y-f2">{statusView}</div>;
};
